import React, { useState, useEffect } from 'react';
import { ChevronDown, Globe, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const COUNTRY_FLAGS = {
  chile: '🇨🇱',
  ecuador: '🇪🇨',
  argentina: '🇦🇷',
  mexico: '🇲🇽',
  colombia: '🇨🇴',
  peru: '🇵🇪'
};

const CountrySelector = ({ onCountryChange, currentCountry = 'chile', compact = false }) => {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (countries.length > 0 && currentCountry) {
      const country = countries.find(c => c.code === currentCountry);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [countries, currentCountry]);

  const loadCountries = async () => {
    try {
      const response = await api.get('/platform/countries');
      setCountries(response.data.countries || []);
      
      // Set default country
      const defaultCountry = response.data.countries?.find(c => c.code === currentCountry) 
        || response.data.countries?.[0];
      if (defaultCountry) {
        setSelectedCountry(defaultCountry);
      }
    } catch (error) {
      console.error('Error loading countries:', error);
      // Fallback to basic list
      setCountries([
        { code: 'chile', name: 'Chile', flag: '🇨🇱', enabled: true },
        { code: 'ecuador', name: 'Ecuador', flag: '🇪🇨', enabled: true },
        { code: 'argentina', name: 'Argentina', flag: '🇦🇷', enabled: true },
        { code: 'mexico', name: 'México', flag: '🇲🇽', enabled: true },
        { code: 'colombia', name: 'Colombia', flag: '🇨🇴', enabled: true },
        { code: 'peru', name: 'Perú', flag: '🇵🇪', enabled: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    
    try {
      await api.post('/platform/user/preferences/country', { country: country.code });
      if (onCountryChange) {
        onCountryChange(country);
      }
    } catch (error) {
      console.error('Error setting country:', error);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-700 rounded-lg h-10 w-32"></div>
    );
  }

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg border border-gray-700 transition-colors"
          data-testid="country-selector-compact"
        >
          <span className="text-xl">{selectedCountry?.flag || '🌎'}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => handleSelect(country)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700 transition-colors text-left ${
                  selectedCountry?.code === country.code ? 'bg-blue-900/30' : ''
                }`}
                data-testid={`country-option-${country.code}`}
              >
                <span className="text-xl">{country.flag}</span>
                <span className="text-white flex-1">{country.name}</span>
                {selectedCountry?.code === country.code && (
                  <Check className="w-4 h-4 text-blue-400" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 rounded-xl border border-gray-600 transition-all shadow-lg w-full"
        data-testid="country-selector"
      >
        <Globe className="w-5 h-5 text-blue-400" />
        <div className="flex items-center gap-2 flex-1">
          <span className="text-2xl">{selectedCountry?.flag || '🌎'}</span>
          <div className="text-left">
            <p className="text-white font-medium">{selectedCountry?.name || 'Seleccionar país'}</p>
            <p className="text-xs text-gray-400">Sistema jurídico activo</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="p-3 border-b border-gray-700">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Seleccionar País</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => handleSelect(country)}
                className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-gray-700/50 transition-colors text-left border-b border-gray-700/50 last:border-0 ${
                  selectedCountry?.code === country.code ? 'bg-blue-900/20' : ''
                }`}
                data-testid={`country-option-${country.code}`}
              >
                <span className="text-3xl">{country.flag}</span>
                <div className="flex-1">
                  <p className="text-white font-medium">{country.name}</p>
                  <p className="text-xs text-gray-400">{country.legal_system || 'Sistema Civil Law'}</p>
                </div>
                {selectedCountry?.code === country.code && (
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CountrySelector;
